import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OrgSwitcher } from './OrgSwitcher'
import { UserMenu } from './UserMenu'
import { SidebarNavItem, type NavItemConfig } from './SidebarNavItem'
import { SidebarNavGroup } from './SidebarNavGroup'
import { useBrandingStore } from '@/stores/branding'

interface Props {
  navItems: NavItemConfig[]
  collapsed: boolean
  onToggle: () => void
  /** Móvil: drawer off-canvas. mobileOpen lo muestra; onMobileClose lo cierra. */
  mobileOpen?: boolean
  onMobileClose?: () => void
}

/**
 * Sidebar lateral premium colapsable (256/64px) con shadow-xl.
 *
 * Estructura (top → bottom):
 *   Header: logo + botón collapse + OrgSwitcher
 *   Nav:    items principales (scrollable si overflow)
 *   Footer: UserMenu (avatar + dropdown)
 *
 * Patrón base: machette-tech packages/app-shell/Sidebar.tsx.
 * §6.1 plan PWA premium.
 */
export function Sidebar({ navItems, collapsed, onToggle, mobileOpen = false, onMobileClose }: Props) {
  const branding = useBrandingStore((s) => s.activo)

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-full flex-col bg-sidebar shadow-xl',
        'transition-[width,transform] duration-300',
        // Escritorio: ancho 256/64 colapsable, siempre visible.
        collapsed ? 'sm:w-16' : 'sm:w-64',
        'sm:translate-x-0',
        // Móvil: drawer de 256px off-canvas (oculto salvo mobileOpen).
        'w-64',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      )}
      style={{ transitionTimingFunction: 'var(--ease-premium)' }}
    >
      {/* Cerrar el drawer en móvil */}
      {onMobileClose && (
        <button
          type="button"
          onClick={onMobileClose}
          aria-label="Cerrar menú"
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-sidebar-accent hover:text-foreground sm:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {/* Header: logo + collapse button + OrgSwitcher */}
      <div className="relative flex flex-col gap-3 px-3 pt-4 pb-2">
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2.5 px-1')}>
          <Link to="/app" aria-label="Inicio">
            {/* Sidebar usa el MARK cuadrado (rostro del águila) — el logo panorámico
                completo se reserva para el login. Un logo custom de cliente, si existe,
                manda en ambos contextos. */}
            <img
              src={branding.logo_url || '/aarpia-eagle-mark.png'}
              alt={branding.nombre_comercial || 'Aarpia'}
              className="h-9 w-9 object-contain transition-all"
            />
          </Link>
          {!collapsed && (
            <span className="truncate text-sm font-semibold tracking-tight text-sidebar-foreground">
              {branding.nombre_comercial || 'Arquitecto'}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expandir menú' : 'Plegar menú'}
          title={collapsed ? 'Expandir menú (Ctrl+B)' : 'Plegar menú (Ctrl+B)'}
          className={cn(
            'absolute top-5 hidden h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground sm:flex',
            collapsed ? '-right-3 shadow-md' : 'right-2',
          )}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>

        <div className={cn(collapsed && 'flex justify-center')}>
          <OrgSwitcher collapsed={collapsed} />
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {navItems.map((item) =>
          item.children && item.children.length > 0 ? (
            <SidebarNavGroup key={item.path} item={item} collapsed={collapsed} />
          ) : (
            <SidebarNavItem key={item.path} item={item} collapsed={collapsed} />
          ),
        )}
      </nav>

      {/* Footer — separado por relieve (bloque elevado), no por línea divisoria. */}
      <div className="mt-1 rounded-lg bg-sidebar-accent/40 px-3 py-3">
        <UserMenu collapsed={collapsed} />
      </div>
    </aside>
  )
}
